import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { TranslateService } from '@ngx-translate/core';
import { 
  AdvancedSearchRequestDTO, 
  AdvancedSearchResponseDTO,
  SearchScope, 
  SearchOperator, 
  SortOption, 
  SortDirection, 
  ContentType,
  SearchResultItemDTO,
  SearchSubMetadataDTO
} from '@/app/models/search.model';

@Component({
    selector: 'app-search-results',
    templateUrl: './search-results.component.html',
    styleUrls: ['./search-results.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchResultsComponent implements OnInit {
  results: SearchResultItemDTO[] = []; // Current active results list
  contentTypeResults: SearchSubMetadataDTO[] = []; // Grouped results from backend
  metadata: any = {
    TotalResults: 0,
    SearchTime: 0,
    Query: ''
  };
  currentContentType: ContentType = ContentType.All;
  currentPage: number = 1;
  currentPageSize: number = 20;
  currentTotalPages: number = 0;
  facets: any[] = [];
  loading = false;
  searchRequest: AdvancedSearchRequestDTO | null = null;
  
  // Search properties for layout
  quickSearchQuery = '';
  searchScope = 0;
  showAdvancedSearch = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const query = params['q'];
      const type = params['type'];

      if (type === 'quick' && query) {
        // Simple quick search
        this.quickSearchQuery = query;
        this.searchScope = Number(params['scope']) || 0;
        this.performQuickSearch(query, this.searchScope);
      } else if (query) {
        // Advanced search
        try {
          this.searchRequest = JSON.parse(query);
          this.quickSearchQuery = this.searchRequest?.SearchTerm || '';
          this.showAdvancedSearch = true; // Gelişmiş arama yapıldığında paneli açık tut
          this.performAdvancedSearch();
        } catch (error) {
          console.error('Invalid search query format:', error);
        }
      }
    });
  }

  performQuickSearch(query: string, scope: number): void {
    this.loading = true;
    const request: AdvancedSearchRequestDTO = {
      SearchTerm: query,
      Scope: SearchScope.All,
      Operator: SearchOperator.AND,
      ContentTypes: scope === 0 ? [ContentType.All] : [scope],
      Page: 1,
      Take: 20,
      SortBy: SortOption.Relevance,
      SortDirection: SortDirection.DESC,
      Pagination: {}
    };

    // Initialize pagination for all content types
    request.Pagination![ContentType.Article] = { Page: 1, Take: 20 };
    request.Pagination![ContentType.Book] = { Page: 1, Take: 20 };
    request.Pagination![ContentType.Journal] = { Page: 1, Take: 20 };
    request.Pagination![ContentType.Author] = { Page: 1, Take: 20 };

    // Save request for pagination
    this.searchRequest = request;

    this.apiService.advancedSearch(request).subscribe({
      next: (response) => {
        this.processResponse(response);
        this.loading = false;
      },
      error: (error) => {
        console.error('Search error:', error);
        this.loading = false;
      }
    });
  }

  performAdvancedSearch(): void {
    if (!this.searchRequest) return;

    this.loading = true;
    this.apiService.advancedSearch(this.searchRequest).subscribe({
      next: (response) => {
        this.processResponse(response);
        this.loading = false;
      },
      error: (error) => {
        console.error('Search error:', error);
        this.loading = false;
      }
    });
  }

  private processResponse(response: AdvancedSearchResponseDTO): void {
    this.contentTypeResults = response.Results || [];
    this.metadata = response.Metadata || this.metadata;
    
    // Fix author names - convert to title case with Turkish support
    this.contentTypeResults.forEach(result => {
      if (result.ContentType === ContentType.Author && result.SearchResultList) {
        result.SearchResultList.forEach(item => {
          if (item.AuthorName) {
            item.AuthorName = this.toTurkishTitleCase(item.AuthorName);
          }
          if (item.Title) {
            item.Title = this.toTurkishTitleCase(item.Title);
          }
        });
      }
    });
    
    if (this.contentTypeResults.length > 0) {
      // Try to find previously selected content type or pick first
      let activeResult = this.contentTypeResults.find(r => r.ContentType === this.currentContentType);
      if (!activeResult) {
        activeResult = this.contentTypeResults[0];
      }
      this.setActiveContentType(activeResult);
    } else {
      this.results = [];
      this.currentTotalPages = 0;
    }
    this.facets = response.Facets || [];
  }

  setActiveContentType(result: SearchSubMetadataDTO): void {
    this.results = result.SearchResultList || [];
    this.currentContentType = result.ContentType;
    this.currentPage = result.Page;
    this.currentPageSize = result.PageSize;
    this.currentTotalPages = result.TotalPages;
  }

  switchTab(type: number): void {
    const result = this.contentTypeResults.find(r => r.ContentType === type);
    if (result) {
      this.setActiveContentType(result);
    }
  }

  getContentTypeLabel(type: number): string {
    switch (type) {
      case ContentType.Article: return 'SEARCH_CONTENT_TYPE_ARTICLE';
      case ContentType.Book: return 'SEARCH_CONTENT_TYPE_BOOK';
      case ContentType.Journal: return 'SEARCH_CONTENT_TYPE_JOURNAL';
      case ContentType.Author: return 'SEARCH_CONTENT_TYPE_AUTHOR';
      default: return 'SEARCH_SCOPE_ALL';
    }
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.currentTotalPages) return;
    
    if (this.searchRequest) {
      // Update pagination for current content type
      if (!this.searchRequest.Pagination) {
        this.searchRequest.Pagination = {};
      }
      
      if (!this.searchRequest.Pagination[this.currentContentType]) {
        this.searchRequest.Pagination[this.currentContentType] = { Page: 1, Take: 20 };
      }
      
      this.searchRequest.Pagination[this.currentContentType].Page = page;
      this.performAdvancedSearch();
      window.scrollTo(0, 0);
    }
  }

  getResultUrl(result: any): string {
    switch (result.Type?.toLowerCase()) {
      case 'article':
        return `/journal/${result.JournalSlug}/article/${result.Slug}`;
      case 'book':
        return `/book/${result.BookSlug}/home`;
      case 'chapter':
        return `/book/${result.BookSlug}/chapter/${result.Slug}`;
      case 'journal':
        return `/journal/${result.JournalSlug || result.Slug}/home`;
      case 'author':
        return `/p/${result.Slug}`;
      default:
        return '#';
    }
  }

  getResultTypeLabel(type: string): string {
    const typeKey = `SEARCH_CONTENT_TYPE_${type?.toUpperCase()}`;
    return this.translate.instant(typeKey);
  }

  getPdfUrl(pdfHash: string): string {
    if (!pdfHash) return '';
    return `https://cdn.istanbul.edu.tr/file/JTA6CLJ8T5/${pdfHash}`;
  }

  stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  highlightText(text: string, highlights: string[]): string {
    if (!highlights || highlights.length === 0) return text;
    
    let highlighted = text;
    highlights.forEach(term => {
      const regex = new RegExp(`(${term})`, 'gi');
      highlighted = highlighted.replace(regex, '<mark>$1</mark>');
    });
    return highlighted;
  }

  navigateForQuickSearch(): void {
    if (this.quickSearchQuery.trim()) {
      this.router.navigate(['/search/results'], {
        queryParams: {
          q: this.quickSearchQuery,
          type: 'quick',
          scope: this.searchScope
        }
      });
    }
  }

  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  /**
   * Converts a string to title case with proper Turkish character handling
   * Example: "FİRDES KAHRAMAN" -> "Firdes Kahraman"
   */
  private toTurkishTitleCase(text: string): string {
    if (!text) return '';
    
    return text
      .toLocaleLowerCase('tr-TR')
      .split(' ')
      .map(word => {
        if (word.length === 0) return word;
        // Use Turkish locale for proper capitalization
        return word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1);
      })
      .join(' ');
  }
}
